import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useAuth, useRoleBuilder } from "hooks";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { SecondaryButton, TertiaryButton } from "components/Buttons";
import { SIZE } from "components/Form";
import { Row } from "components/Containers";
import Tabs from "components/Tabs";
import { colors, margins } from "style";
import {
  BLUEPRINT_TAB,
  JOB_DESCRIPTION_TAB,
  ROLE_BUILDER_STEP,
  getAwaitingTaskFields,
  getBlueprintFields,
  getInformationFields,
  getJobDescriptionReviewFields,
  getJobDescriptionUploadFields,
  isDraftInProgress,
} from "constants/index";
import RoleReview from "./RoleReview";
import BuilderSidebar from "./BuilderSidebar";
import BlueprintVetting from "./BlueprintVetting";
import { RoleBuilderContext } from "./RoleBuilderContext"; // To avoid circular dependency

/**
 * RoleBuilderModal
 *
 * The Talent Simple role builder. Its first step submits a job description to the AI role
 * builder, waits for it, then reviews what came back; accepting the review creates the role
 * as an unfinished draft the customer can return to.
 *
 * @param {Boolean}   show         Whether the modal is visible
 * @param {Function}  onClose      Called when the modal should close
 * @param {Object}    task         An AI role builder task to review, when resuming
 * @param {Object}    role         An unfinished draft being resumed from the roles list
 * @param {String}    missionId    Mission the role is created under
 * @param {String}    companyName  Company the role is for, from the organization
 * @param {String}    companyUrl   Company site, from the organization
 */
const RoleBuilderModal = ({ show, onClose, task, role, missionId, companyName, companyUrl }) => {
  const builder = useRoleBuilder({ task, role, missionId, companyName, companyUrl, onClose });
  const { domainId } = useAuth();
  const {
    activeStep,
    stepDescriptionId,
    tabTitleIds,
    selectedTab,
    setSelectedTab,
    isJobDescriptionSent,
    isReviewStep,
    isSubmitted,
    awaitingTask,
    vettingRole,
    vettingLoading,
    refetchVetting,
    canGoBack,
    goBack,
    canContinueLater,
    continueLater,
    handleError,
    canSubmit,
    submitting,
    formProps,
  } = builder;

  return (
    <RoleBuilderContext.Provider value={builder}>
      <ModalForm
        show={show}
        onClose={onClose}
        closeOnComplete={false}
        onError={handleError}
        size={MODAL_SIZE.xlarge}
        title={<FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Title" />}
        description={<FormattedMessage id={stepDescriptionId} />}
        primaryButtonMessageId={getPrimaryButtonMessageId({ isReviewStep, isSubmitted })}
        data={getFormData({
          activeStep,
          selectedTab,
          isJobDescriptionSent,
          awaitingTask,
          vettingRole,
          vettingLoading,
          refetchVetting,
          domainId: vettingRole?.domain?.id || domainId,
        })}
        showCancel={!canContinueLater}
        showSave={!awaitingTask}
        canSubmit={canSubmit}
        loading={submitting}
        footer={({ submitPartial, loading }) =>
          canGoBack || canContinueLater ? (
            <Row>
              {canGoBack && (
                <TertiaryButton type="button" onClick={goBack} disabled={loading}>
                  <FormattedMessage id="Global.Back" />
                </TertiaryButton>
              )}
              {canContinueLater && (
                <SecondaryButton type="button" onClick={() => continueLater(submitPartial)} disabled={loading}>
                  <FormattedMessage id="Mission.RoleForm.ContinueLater.CTA" />
                </SecondaryButton>
              )}
            </Row>
          ) : null
        }
        {...formProps}
      >
        {tabTitleIds && (
          <div css={styles.tabs}>
            <Tabs
              data={tabTitleIds.map((id) => (
                <FormattedMessage key={id} id={id} />
              ))}
              selected={selectedTab}
              onSelect={setSelectedTab}
            />
          </div>
        )}
      </ModalForm>
    </RoleBuilderContext.Provider>
  );
};

const getPrimaryButtonMessageId = ({ isReviewStep, isSubmitted }) => {
  if (!isReviewStep) return "Missions.Roles.TalentSimple.RoleBuilder.Next.CTA";

  return isSubmitted ? "Global.Save" : "Missions.Roles.TalentSimple.RoleBuilder.Submit.CTA";
};

const getFormData = (context) => [
  { style: styles.form_main, items: getStepFields(context) },
  { style: styles.form_side, items: [{ type: BuilderSidebar, size: SIZE.xlarge }] },
];

const getStepFields = ({
  activeStep,
  selectedTab,
  isJobDescriptionSent,
  awaitingTask,
  vettingRole,
  vettingLoading,
  refetchVetting,
  domainId,
}) => {
  // The vetting modules and the review read the draft back, and save through their own mutations.
  const vettingProps = { role: vettingRole, loading: vettingLoading };

  if (activeStep === ROLE_BUILDER_STEP.BLUEPRINT && selectedTab === BLUEPRINT_TAB.customize) {
    return [
      {
        type: BlueprintVetting,
        size: SIZE.xlarge,
        properties: {
          ...vettingProps,
          refetch: refetchVetting,
          canRemoveRequirements: isDraftInProgress(vettingRole?.state),
        },
      },
    ];
  }

  if (activeStep === ROLE_BUILDER_STEP.REVIEW)
    return [{ type: RoleReview, size: SIZE.xlarge, properties: vettingProps }];

  if (activeStep === ROLE_BUILDER_STEP.BLUEPRINT) return getBlueprintFields();

  if (activeStep === ROLE_BUILDER_STEP.INFORMATION) return getInformationFields({ domainId });

  if (selectedTab === JOB_DESCRIPTION_TAB.upload)
    return getJobDescriptionUploadFields({ disabled: isJobDescriptionSent });

  if (awaitingTask) return getAwaitingTaskFields(awaitingTask);

  return getJobDescriptionReviewFields();
};

const styles = {
  tabs: css`
    margin-bottom: ${margins.xsmall};
  `,
  form_main: css`
    width: 65%;
    padding-right: 4rem !important;
    border-right: 1px solid ${colors.grayAnatomyLight4};
  `,
  form_side: css`
    width: 35%;
    padding-left: 4rem !important;
  `,
};

RoleBuilderModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  task: PropTypes.object,
  role: PropTypes.object,
  missionId: PropTypes.string,
  companyName: PropTypes.string,
  companyUrl: PropTypes.string,
};

export default RoleBuilderModal;
